import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { signup, login, verifyOtp, resendOtp } from "../../store/auth/authSlice";
import { EyeIcon, EyeOffIcon } from "./PasswordEyeIcons";

export default function AuthOverlay({
  open,
  initialMode = "login",
  onClose,
  onLoginSuccess,
  onOpenForgotPassword,
}) {
  const dispatch = useAppDispatch();
  const authStatus = useAppSelector((s) => s.auth.status);
  const authError = useAppSelector((s) => s.auth.error);
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState("form"); // form | otp | success
  const [otp, setOtp] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [otpError, setOtpError] = useState("");
  const [otpInfo, setOtpInfo] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const emptyForm = useMemo(
    () => ({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    }),
    []
  );
  const [form, setForm] = useState(emptyForm);

  const resetFormFields = useCallback(() => {
    setForm(emptyForm);
    setFieldErrors({});
    setFormError("");
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [emptyForm]);

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setStep("form");
    setOtp("");
    setOtpError("");
    setOtpInfo("");
    resetFormFields();
  }, [open, initialMode, resetFormFields]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (!open) return undefined;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const isLogin = mode === "login";
  const primaryLabel =
    step === "form" ? (isLogin ? "Login" : "Sign Up") : step === "otp" ? "Validate OTP" : "Go to Home";

  async function submitForm(e) {
    e.preventDefault();
    setFormError("");
    setFieldErrors({});

    const nextErrors = {};

    // Common email/password validation
    if (!form.email?.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!form.password) nextErrors.password = "Password is required.";

    // Extra checks for signup
    if (!isLogin) {
      if (!form.name?.trim()) {
        nextErrors.name = "Full Name is required.";
      }
      if (!form.confirmPassword) {
        nextErrors.confirmPassword = "Confirm Password is required.";
      } else if (form.password && form.password !== form.confirmPassword) {
        nextErrors.confirmPassword = "Passwords do not match.";
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    try {
      if (isLogin) {
        await dispatch(
          login({
            email: form.email,
            password: form.password,
          })
        ).unwrap();
        onLoginSuccess?.();
      } else {
        await dispatch(
          signup({
            full_name: form.name,
            email: form.email,
            password: form.password,
            password_confirmation: form.confirmPassword,
          })
        ).unwrap();
        setStep("otp");
      }
    } catch (err) {
      setFormError(typeof err === "string" ? err : "Signup failed.");
    }
  }

  function submitOtp(e) {
    e.preventDefault();
    setOtpError("");

    const cleaned = (otp || "").replace(/\D/g, "");
    if (cleaned.length !== 4) {
      setOtpError("Please enter the 4 digit OTP.");
      return;
    }

    dispatch(verifyOtp({ otp: cleaned }))
      .unwrap()
      .then(() => {
        setStep("success");
      })
      .catch((err) => {
        setOtpError(
          typeof err === "string" ? err : "Invalid OTP. Please try again."
        );
      });
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close authentication modal"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-xl">
          {step === "success" ? null : step === "otp" ? (
            <div className="relative px-6 pb-2 pt-6">
              <button
                type="button"
                onClick={() => {
                  setStep("form");
                  setOtp("");
                }}
                className="absolute left-6 top-6 rounded-full p-2 text-slate-700 hover:bg-slate-100"
                aria-label="Back"
              >
                ←
              </button>
              <p className="text-center text-3xl font-extrabold tracking-tight text-slate-900">
                Validate OTP
              </p>
            </div>
          ) : (
            <div className="relative flex items-center justify-center px-6 pt-5">
              {/* Center Title */}
              <p className={`font-extrabold text-slate-900 ${step === "form" ? "text-3xl" : "text-2xl"}`}>
                {step === "form"
                  ? isLogin
                    ? "Sign In"
                    : "Sign Up"
                  : "Verified"}
              </p>

              {/* Right Close Button */}
              <button
                type="button"
                onClick={onClose}
                className="absolute right-6 rounded-full p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          )}

          {/* {step === "form" && (
            <div className="px-6 pt-4">
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-1 text-xs">
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className={cx(
                    "rounded-lg px-3 py-2 font-semibold transition",
                    mode === "login" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className={cx(
                    "rounded-lg px-3 py-2 font-semibold transition",
                    mode === "signup" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  )}
                >
                  Sign up
                </button>
              </div>
            </div>
          )} */}

          <div className="px-6 pb-6 pt-4">
            {step === "success" ? (
              <div className="flex flex-col items-center text-center pt-8 pb-4">
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-emerald-500">
                  <span className="text-3xl text-emerald-500">✔</span>
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900">
                  Congratulations
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Your account has been verified.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-8 w-full rounded-sm bg-[#4F46E5] py-3 text-sm font-semibold text-white hover:bg-[#4F46E5]"
                >
                  Home <span className="ml-4">→</span>
                </button>
              </div>
            ) : step === "otp" ? (
              <form onSubmit={submitOtp} className="space-y-6 pt-2">
                <div className="text-center">
                  <p className="text-base text-[#858C99]">
                    Enter 4 digit OTP sent to (email/phone number)
                  </p>
                  {otpInfo && (
                    <p className="mt-2 text-xs text-emerald-600">{otpInfo}</p>
                  )}
                </div>

                <div>
                  <input
                    value={otp}
                    onChange={(e) =>
                      setOtp((e.target.value || "").replace(/\D/g, "").slice(0, 4))
                    }
                    inputMode="numeric"
                    placeholder="OTP"
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2 text-center text-xl text-slate-700 shadow-sm focus:border-indigo-500 focus:outline-none"
                  />
                  {otpError && (
                    <p className="mt-1 text-xs text-red-600">{otpError}</p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full rounded-sm bg-[#4F46E5] py-2 text-lg font-semibold text-white hover:bg-[#4F46E5] disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={authStatus === "loading"}
                >
                  {authStatus === "loading" ? "Please wait..." : "Verify →"}
                </button>

                <button
                  type="button"
                  className="mx-auto block text-base font-semibold text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={authStatus === "loading"}
                  onClick={() => {
                    setOtpError("");
                    setOtpInfo("");
                    dispatch(resendOtp())
                      .unwrap()
                      .then((data) => {
                        setOtpInfo(
                          data?.message || "OTP resent successfully."
                        );
                      })
                      .catch((err) => {
                        setOtpError(
                          typeof err === "string"
                            ? err
                            : "Failed to resend OTP. Please try again."
                        );
                      });
                  }}
                >
                  Resend OTP
                </button>
              </form>
            ) : (
              <form onSubmit={submitForm} className="space-y-4">
              {!!(formError || authError) && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 whitespace-pre-line">
                  {formError || authError}
                </div>
              )}
              {!isLogin && (
                <div>
                  <input
                    value={form.name}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, name: e.target.value }))
                    }
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                    placeholder="Full Name"
                  />
                  {fieldErrors.name && (
                    <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>
                  )}
                </div>
              )}
            
              <div>
                <input
                  value={form.email}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, email: e.target.value }))
                  }
                  className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                  placeholder="Email"
                  type="email"
                />
                {fieldErrors.email && (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>
                )}
              </div>
            
              <div>
                <div className="relative">
                  <input
                    value={form.password}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, password: e.target.value }))
                    }
                    className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-3 flex items-center text-slate-400"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.password}
                  </p>
                )}
              </div>
            
              {!isLogin && (
                <div>
                  <div className="relative">
                    <input
                      value={form.confirmPassword}
                      onChange={(e) =>
                        setForm((s) => ({
                          ...s,
                          confirmPassword: e.target.value,
                        }))
                      }
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                      placeholder="Confirm Password"
                      type={showConfirmPassword ? "text" : "password"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400"
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showConfirmPassword ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {fieldErrors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      {fieldErrors.confirmPassword}
                    </p>
                  )}
                </div>
              )}
            
              {/* Login Button */}
              <button
                type="submit"
                className="w-full rounded-sm bg-[#4F46E5] py-3 text-sm font-semibold text-white hover:bg-[#4F46E5] disabled:cursor-not-allowed disabled:opacity-60"
                disabled={authStatus === "loading"}
              >
                {authStatus === "loading"
                  ? "Please wait..."
                  : `${primaryLabel} →`}
              </button>
            
              {/*Forgot Password (Only for Login) */}
              <div className="flex justify-between">
              {isLogin && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => {
                      onClose?.();
                      onOpenForgotPassword?.();
                    }}
                    className="text-sm text-slate-700 hover:text-indigo-600"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
            
              {/* Switch Login / Signup */}
              <div className="text-sm text-slate-500 text-center">
                {isLogin ? (
                  <>
                    Don’t have an account?{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setMode("signup");
                        resetFormFields();
                      }}
                      className="font-semibold text-slate-900 hover:text-indigo-600"
                    >
                      Sign up
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setMode("login");
                        resetFormFields();
                      }}
                      className="font-semibold text-slate-900 hover:text-indigo-600"
                    >
                      Sign in now
                    </button>
                  </>
                )}
              </div>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

